import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MatchHistoryTable from './MatchHistoryTable';
import Rating3Badge from '../common/Rating3Badge';
import ImpactRatingBadge from '../common/ImpactRatingBadge';

const ProfileSummaryTab = ({ 
  last25MatchData, 
  matchSummary,
  recentStats,
  rating3,
  impactRating,
  sortOptions,
  hasPlayedAtLeastOneMatch,
  isLoggedIn,
}) => {
  // Get last 5 matches for summary
  const last5Matches = React.useMemo(() => {
    if (!last25MatchData) return {};
    
    const matchesArray = Object.entries(last25MatchData)
      .map(([id, data]) => ({ id, ...data }))
      .sort((a, b) => b.timestamp - a.timestamp)
      .slice(0, 5);
    
    const last5 = {};
    matchesArray.forEach(match => {
      last5[match.id] = match;
    });
    
    return last5;
  }, [last25MatchData]);

  return (
    <Box>
      {/* Quick Stats Cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(5, 1fr)',
          gap: '1.5rem',
          marginBottom: '2rem',
          '@media (max-width: 1200px)': {
            gridTemplateColumns: 'repeat(3, 1fr)',
          },
          '@media (max-width: 768px)': {
            gridTemplateColumns: 'repeat(2, 1fr)',
          },
          '@media (max-width: 600px)': {
            gridTemplateColumns: '1fr',
          },
        }}
      >
        <Box
          sx={{
            backgroundColor: '#1C1E20',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: '4px',
            padding: '1.5rem',
            textAlign: 'center',
          }}
        >
          <Typography
            sx={{
              fontSize: '0.75rem',
              color: '#999999',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: '0.5rem',
            }}
          >
            Matches
          </Typography>
          <Typography
            sx={{
              fontSize: '2rem',
              fontFamily: "'IBM Plex Mono', monospace",
              fontWeight: 700,
              color: '#ffffff',
            }}
          >
            {matchSummary?.totalPlayedMatches || 0}
          </Typography>
        </Box>

        <Box
          sx={{
            backgroundColor: '#1C1E20',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: '4px',
            padding: '1.5rem',
            textAlign: 'center',
          }}
        >
          <Typography
            sx={{
              fontSize: '0.75rem',
              color: '#999999',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: '0.5rem',
            }}
          >
            Win Rate
          </Typography>
          <Typography
            sx={{
              fontSize: '2rem',
              fontFamily: "'IBM Plex Mono', monospace",
              fontWeight: 700,
              color: matchSummary?.winRate >= 50 ? '#4CAF50' : '#F44336',
            }}
          >
            {matchSummary?.winRate || 0}%
          </Typography>
        </Box>

        <Box
          sx={{
            backgroundColor: '#1C1E20',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: '4px',
            padding: '1.5rem',
            textAlign: 'center',
          }}
        >
          <Typography
            sx={{
              fontSize: '0.75rem',
              color: '#999999',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: '0.5rem',
            }}
          >
            AVG ADR
          </Typography>
          <Typography
            sx={{
              fontSize: '2rem',
              fontFamily: "'IBM Plex Mono', monospace",
              fontWeight: 700,
              color: '#ffffff',
            }}
          >
            {recentStats?.adr || '-'}
          </Typography>
        </Box>

        <Box
          sx={{
            backgroundColor: '#1C1E20',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: '4px',
            padding: '1.5rem',
            textAlign: 'center',
          }}
        >
          <Typography
            sx={{
              fontSize: '0.75rem',
              color: '#999999',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: '0.5rem',
            }}
          >
            K/D Ratio
          </Typography>
          <Typography
            sx={{
              fontSize: '2rem',
              fontFamily: "'IBM Plex Mono', monospace",
              fontWeight: 700,
              color: '#ffffff',
            }}
          >
            {recentStats?.kd || '-'}
          </Typography>
        </Box>

        <Box
          sx={{
            backgroundColor: '#1C1E20',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: '4px',
            padding: '1.5rem',
            textAlign: 'center',
          }}
        >
          <Typography
            sx={{
              fontSize: '0.75rem',
              color: '#999999',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: '0.5rem',
            }}
          >
            Rating 3.0
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Rating3Badge value={rating3?.current ?? null} size="large" />
          </Box>
          {rating3?.peak != null && (
            <Typography sx={{ fontSize: '0.7rem', color: '#666', marginTop: '0.25rem' }}>
              Peak: {rating3.peak.toFixed(2)}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            backgroundColor: '#1C1E20',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: '4px',
            padding: '1.5rem',
            textAlign: 'center',
          }}
        >
          <Typography
            sx={{
              fontSize: '0.75rem',
              color: '#999999',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: '0.5rem',
            }}
          >
            Impact Rating
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <ImpactRatingBadge value={impactRating?.current ?? null} components={impactRating?.components || null} size="large" />
          </Box>
          {impactRating?.peak != null && (
            <Typography sx={{ fontSize: '0.7rem', color: '#666', marginTop: '0.25rem' }}>
              Peak: {impactRating.peak.toFixed(2)}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Recent Matches */}
      <Box>
        <Typography
          sx={{
            fontSize: '1.1rem',
            fontFamily: "'IBM Plex Mono', monospace",
            fontWeight: 600,
            color: '#ffffff',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            marginBottom: '1.5rem',
          }}
        >
          Recent Matches
        </Typography>
        
        <MatchHistoryTable
          rows={last5Matches}
          areMatchesAvailable={hasPlayedAtLeastOneMatch}
          sortOptions={sortOptions}
          hideSort={true}
          isLoggedIn={isLoggedIn}
        />
      </Box>
    </Box>
  );
};

ProfileSummaryTab.propTypes = {
  last25MatchData: PropTypes.object,
  matchSummary: PropTypes.object,
  recentStats: PropTypes.object,
  rating3: PropTypes.object,
  impactRating: PropTypes.object,
  sortOptions: PropTypes.object,
  hasPlayedAtLeastOneMatch: PropTypes.bool.isRequired,
  isLoggedIn: PropTypes.bool.isRequired,
};

export default ProfileSummaryTab;
